import { emitGameAction, onGameAction, offGameAction } from './socketClient';

/**
 * Strip identity fields from a cardInstance before broadcasting a
 * face-down placement. The opponent still needs render-position info
 * (id, isSite, coords, tapped flag) to spawn and track the mesh, but
 * must never receive name / catalog id / imageUrl. That would make
 * face-down play pointless because a devtools-savvy opponent could
 * inspect network traffic to read the hidden card.
 *
 * Keeping `isSite` is a deliberate trade-off: sites are visibly
 * landscape-oriented and use a different card back in real play, so
 * the orientation is public information regardless. Not sending it
 * would force the opponent's mesh to guess orientation.
 */
function sanitizeForFaceDown(cardInstance) {
  return {
    ...cardInstance,
    name: null,
    cardId: null,
    imageUrl: null,
    // Foiling is visible through the sleeve in real play via reflections;
    // here we scrub it so the opponent can't distinguish a foil from a
    // non-foil face-down card through shader hints.
    foiling: null,
  };
}


/**
 * GameSyncBridge. The single source of truth for every outbound game
 * action, and the single place where inbound game action handlers are
 * registered.
 *
 * Why this exists:
 * Before this class, GameBoard.jsx made ~45 raw `emitGameAction(type, data)`
 * calls scattered through the file, each re-checking `this.suppressBroadcast`
 * and each hand-crafting its payload. Every new synced action required
 * finding the right pattern to copy; any missed `suppressBroadcast` guard
 * caused a re-emit loop. This class gives every action exactly one home.
 *
 * Design:
 *   - Typed methods per message type (claimCard, streamCardPose, firePing, …).
 *   - Internal suppressBroadcast flag. Callers don't check it. They call
 *     the method and trust the bridge to skip the emit if suppressed.
 *   - `withSuppressed(fn)` wraps a synchronous block. For calls that need
 *     to suppress across an async boundary (e.g. restoreSession), use the
 *     setSuppressed(bool) escape hatch.
 *   - registerHandlers() accepts a flat { type: fn } map and tracks the
 *     registrations so destroy() can clean them up on unmount.
 */
export class GameSyncBridge {
  constructor() {
    this._suppressed = false;
    this._handlers = [];
  }

  // --- Suppression -----------------------------------------------------

  /** Run a synchronous block with outbound broadcasts suppressed. */
  withSuppressed(fn) {
    const prev = this._suppressed;
    this._suppressed = true;
    try {
      return fn();
    } finally {
      this._suppressed = prev;
    }
  }

  /** Raw flag setter for operations that span async boundaries. */
  setSuppressed(value) {
    this._suppressed = !!value;
  }

  get isSuppressed() {
    return this._suppressed;
  }

  // --- Internal send ---------------------------------------------------

  _send(type, data) {
    if (this._suppressed) return;
    // Optional recording tap. The MatchRecorder subscribes here to buffer
    // every outbound action for replay/analytics. Runs only on real,
    // non-suppressed sends so it mirrors exactly what crosses the wire.
    if (typeof this.onAnySend === 'function') {
      try { this.onAnySend(type, data); } catch { /* recording must never break sends */ }
    }
    emitGameAction(type, data);
  }

  // --- Card mutations --------------------------------------------------

  /**
   * Place a card on the table. Payload carries the full cardInstance.
   * Pass `staged: true` when the card is merely being staged for a
   * drag (e.g. played out of hand, drawn from a deck). The opponent
   * will then add the mesh silently and skip the play-commit side
   * effects; a later `card:played` event will fire them once the
   * drag actually ends on the table.
   *
   * Face-down cards are sanitized before send. The opponent receives
   * only the render-position fields and never learns the card's name,
   * catalog id, or image url.
   *
   * A STAGED placement is sanitized too, and forced face-down on the
   * wire regardless of the local face state. Staging fires the moment
   * the cursor leaves the hand container (handState.commitHandDragToTable)
   * Mouse still held, play not yet committed. So sending the real
   * identity there showed the opponent (and every spectator) a card the
   * player could still drag back into hand. The opponent tracks a card
   * back through the drag. Only the wire copy is masked; the local
   * instance is untouched, so the actor keeps seeing its own card face.
   *
   * Nothing re-broadcasts the identity afterwards: the drop moves the card
   * out of the actor's hand server-side, so the per-seat projection stops
   * stripping it and the opponent learns `cardId` over the authoritative
   * diff. authReveals.foldRevealedIdentity resolves the face from there.
   */
  placeCard(cardInstance, { staged = false, silent = false, logVerb = null } = {}) {
    const toSend = (cardInstance.faceDown || staged)
      ? { ...sanitizeForFaceDown(cardInstance), faceDown: true }
      : cardInstance;
    // `silent` suppresses the receiver's "Played X" log line. Used
    // when the placement is a side-effect (e.g. equipment auto-
    // detaching off a host card that's leaving the field) where the
    // sender will broadcast a more accurate log line separately.
    //
    // `logVerb` overrides the "Played" prefix on both ends. Used by
    // the StatusTokenDrawer to log "Spawned <name>" instead. Same
    // wording on the sender and receiver so the two logbooks stay in
    // sync.
    this._send('card:place', { cardInstance: toSend, staged, silent, logVerb });
  }


  /**
   * Send a free-text chat message that appears inline in the opponent's
   * logbook. Ephemeral. Never stored server-side beyond the live room.
   */
  sendChatMessage(text) {
    this._send('log:chat', { text });
  }

  /**
   * Mirror an activated ability onto the opponent's storyline as a
   * read-only acknowledge entry. Async/informational. The caster
   * does NOT wait for the opponent to acknowledge.
   */
  abilityActivatedNotice({ cardId, cardName, imageUrl, description, controller }) {
    this._send('storyline:ability', { cardId, cardName, imageUrl, description, controller });
  }

  /**
   * Broadcast a logbook entry. The sender's local append is done by
   * GameBoard.logAction / logSystem; this is the wire half only.
   *
   * `system: true` is for game-rule announcements (Death's Door, turn
   * banners, GAME OVER, dice-roll results, advisories) that render
   * centered without a player-name prefix on both clients. Receivers
   * append with actor='system' rather than 'opponent' so the styling
   * matches the sender's side verbatim.
   */
  logbookEntry(message, kind = 'action', { system = false } = {}) {
    const { key, values, text } = typeof message === 'string' ? { text: message } : message;
    this._send('logbook:entry', { text, key, values, kind, system });
  }

  /** Remove a card from the table by id. `toPileId` (optional) names
   *  the destination pile so the receiver can play the same
   *  fly-to-pile flight instead of an instant despawn. */
  removeCard(cardId, toPileId = null) {
    this._send('card:remove', { cardId, toPileId });
  }

  /**
   * Claim ownership of a card. Sent the moment a local action wakes up
   * the card's body (drag start, or auto-claim from a cascade collision).
   * Receivers flip the body to kinematic + non-colliding so it can't
   * fight the incoming pose stream.
   *
   * `interactionOrder` (optional): the freshly-allocated stacking order
   * for this card. Receivers apply it to their copy and advance their
   * local counter so subsequent local claims always sort above. Omitted
   * when the claim is just an ownership change (auto-claim cascade)
   * with no stacking-rank intent.
   */
  claimCard(cardId, interactionOrder) {
    const payload = { cardId };
    if (typeof interactionOrder === 'number') {
      payload.interactionOrder = interactionOrder;
    }
    this._send('card:claim', payload);
  }

  /**
   * Stream the current physics pose of a locally-owned card. Throttled
   * to ~30 Hz by the caller via CardOwnership.shouldBroadcast. The pose
   * arrays are flat [x, y, z] and [x, y, z, w] for cheap JSON encoding.
   */
  streamCardPose(cardId, pos, quat) {
    this._send('card:pose', { cardId, pos, quat });
  }

  /**
   * Release ownership and report the final settled pose. Sent when the
   * card's body sleeps after the local player stopped interacting.
   * Receivers apply the pose, sleep the body, and return it to dynamic
   * + colliding so future actions on either side can wake it again.
   */
  releaseCard(cardId, pos, quat, extras = null) {
    // `extras` is an optional payload of cardInstance fields the
    // receiver should re-apply after pose. Used by programmatic
    // aura moves (Wildfire) to sync state that isn't part of the
    // standard mesh broadcast. Currently `_visitedCells`.
    const msg = { cardId, pos, quat };
    if (extras) msg.extras = extras;
    this._send('card:release', msg);
  }

  /** Broadcast which card the local player is hovering or inspecting.
   *  Pass `null` to clear the highlight on the opponent's board. */
  hoverCard(cardId) {
    this._send('card:hover', { cardId });
  }

  // --- Groups ----------------------------------------------------------

  /**
   * Broadcast that a set of cards on the table have been grouped together
   * under a shared groupId. Receivers write the groupId onto each card
   * instance and rebuild their groups map so the cards drag together on
   * the opponent's board too. Group membership is fully replaceable.
   * sending the same groupId with a different card list replaces it.
   */
  setGroup(groupId, cardIds) {
    this._send('group:set', { groupId, cardIds });
  }

  /** Dissolve a group. Receivers clear the groupId from every member. */
  clearGroup(groupId) {
    this._send('group:clear', { groupId });
  }

  // --- Ping ----------------------------------------------------------

  /**
   * Fire a board ping at a world-space point. Ephemeral: ping events
   * are never persisted or replayed on reconnect. The receiver renders
   * a brief ring on receipt and that's it.
   *
   * `t` is the sender's Date.now() at fire time; receivers use it to
   * drop stale messages (network burps, reconnect floods) so a ping
   * doesn't pop in seconds after the moment it referenced.
   */
  firePing({ pingId, x, y, z, t }) {
    this._send('ping:fire', { pingId, x, y, z, t });
  }

  // Match-result handshake (proposeMatch / confirmMatch / rejectMatch)
  // is gone. Outcome arbitration is server-mediated now, see
  // utils/arena/matchReporting.js + the /matches/:roomCode/report
  // endpoint. Clients no longer relay outcomes peer-to-peer.

  // --- Legacy --------------------------------------------------------

  /** Raw escape hatch for messages that don't yet have a typed method. */
  emit(type, data) {
    this._send(type, data);
  }

  // --- Inbound handler registration ---------------------------------

  /**
   * Register a flat map of { 'message:type': handler } subscriptions.
   * All registrations are tracked so destroy() can unsubscribe them.
   */
  registerHandlers(handlers) {
    for (const [event, fn] of Object.entries(handlers)) {
      onGameAction(event, fn);
      this._handlers.push({ event, fn });
    }
  }

  /** Unsubscribe everything registered via registerHandlers. */
  destroy() {
    for (const { event, fn } of this._handlers) {
      try { offGameAction(event, fn); } catch {}
    }
    this._handlers = [];
    this._suppressed = false;
  }
}
